<script setup lang="ts">
import type { ChartSeries } from '@/lib/charts/types'
import type { Customer, HealthReport, InstanceHealth, MetricSeriesData, MetricValue, Product } from '@/types'
import { Deferred, Head, Link, router } from '@inertiajs/vue3'
import { computed } from 'vue'
import ChartPanel from '@/components/charts/ChartPanel.vue'
import TimeRangeToolbar from '@/components/charts/TimeRangeToolbar.vue'
import TimeSeriesChart from '@/components/charts/TimeSeriesChart.vue'
import HealthBadge from '@/components/telemetry/HealthBadge.vue'
import { provideTimeRange } from '@/composables/useTimeRange'
import { formatRelative, healthPresentation } from '@/lib/health'
import { dashboardLayout } from '@/lib/layouts'
import { adminCustomerPath, adminProductPath } from '@/routes'

defineOptions({ layout: dashboardLayout })

const props = defineProps<{
  instance: InstanceDetail
  health: InstanceHealth
  heartbeats: HealthReport[]
  metrics?: MetricSeriesData[]
}>()

interface InstanceDetail {
  id: number
  identifier: string | null
  metadata: Record<string, unknown> | null
  reported_version: string | null
  reported_at: string | null
  created_at: string
  customer: Pick<Customer, 'id' | 'name' | 'active'>
  product: Pick<Product, 'id' | 'name' | 'slug'>
  channel: { id: number, name: string } | null
}

const timeRange = provideTimeRange({ only: ['metrics'] })

const palette = ['var(--ui-primary)', '#f59e0b', '#10b981', '#6366f1', '#ec4899', '#14b8a6']

const presentation = computed(() => healthPresentation(props.health.state))
const latest = computed(() => props.health.latest)
const hasReported = computed(() => latest.value !== null)

interface MetricChart {
  key: string
  kind: 'number' | 'boolean'
  latest: MetricValue | null
  series: ChartSeries[]
}

const charts = computed<MetricChart[]>(() =>
  (props.metrics ?? []).map((metric, index) => ({
    key: metric.key,
    kind: metric.kind,
    latest: metric.latest,
    series: [{
      id: metric.key,
      label: metric.key,
      color: palette[index % palette.length],
      type: 'area',
      points: metric.points,
    }],
  })),
)

function formatMetricValue(value: MetricValue | null, kind: 'number' | 'boolean'): string {
  if (value === null)
    return '—'
  if (kind === 'boolean')
    return value ? 'Yes' : 'No'
  return new Intl.NumberFormat('en', { maximumFractionDigits: 2 }).format(Number(value))
}

function chartFormatter(kind: 'number' | 'boolean') {
  return (value: number) => (kind === 'boolean' ? (value >= 0.5 ? 'Yes' : 'No') : formatMetricValue(value, 'number'))
}

function metricSummary(report: HealthReport): string {
  const entries = Object.entries(report.metrics)
  if (entries.length === 0)
    return '—'
  return entries
    .map(([key, value]) => `${key}: ${typeof value === 'boolean' ? (value ? 'yes' : 'no') : value}`)
    .join(' · ')
}

function formatTimestamp(value: string): string {
  return new Date(value).toLocaleString()
}

// Return to wherever the instance was opened from (product telemetry, customer,
// fleet timeline…) rather than always forcing the customer page.
function goBack(): void {
  if (window.history.length > 1)
    window.history.back()
  else
    router.visit(adminProductPath(props.instance.product.slug))
}
</script>

<template>
  <Head :title="`${instance.product.name} · ${instance.customer.name}`" />

  <UDashboardPanel id="instance-show">
    <template #header>
      <UDashboardNavbar :title="`${instance.product.name} health`" :ui="{ right: 'gap-3' }">
        <template #leading>
          <UButton
            icon="i-lucide-arrow-left"
            color="neutral"
            variant="ghost"
            square
            aria-label="Back"
            @click="goBack"
          />
        </template>
        <template #right>
          <TimeRangeToolbar :controller="timeRange" />
          <HealthBadge :state="health.state" size="md" />
        </template>
      </UDashboardNavbar>
    </template>

    <template #body>
      <div class="flex flex-col gap-8">
        <!-- Snapshot -->
        <div class="ring-default flex flex-col gap-5 rounded-lg p-5 ring">
          <div class="flex items-start gap-4">
            <div class="bg-elevated flex size-11 shrink-0 items-center justify-center rounded-lg">
              <UIcon :name="presentation.icon" class="size-6" :style="{ color: presentation.chartColor }" />
            </div>
            <div class="min-w-0">
              <p class="text-highlighted text-lg font-semibold">
                {{ presentation.label }}
              </p>
              <p class="text-muted text-sm">
                <Link :href="adminCustomerPath(instance.customer.id)" class="hover:underline">
                  {{ instance.customer.name }}
                </Link>
                ·
                <Link :href="adminProductPath(instance.product.slug)" class="hover:underline">
                  {{ instance.product.name }}
                </Link>
              </p>
            </div>
          </div>

          <USeparator />

          <dl class="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <dt class="text-muted text-xs uppercase tracking-wide">
                Reported version
              </dt>
              <dd class="text-highlighted font-mono">
                {{ latest?.version || instance.reported_version || '—' }}
              </dd>
            </div>
            <div>
              <dt class="text-muted text-xs uppercase tracking-wide">
                Last seen
              </dt>
              <dd class="text-highlighted">
                {{ formatRelative(latest?.created_at ?? instance.reported_at) }}
              </dd>
            </div>
            <div>
              <dt class="text-muted text-xs uppercase tracking-wide">
                Channel
              </dt>
              <dd class="text-highlighted">
                {{ instance.channel?.name || 'Unassigned' }}
              </dd>
            </div>
            <div>
              <dt class="text-muted text-xs uppercase tracking-wide">
                Reported gauges
              </dt>
              <dd class="text-highlighted">
                {{ charts.length }}
              </dd>
            </div>
          </dl>

          <p v-if="instance.identifier" class="text-dimmed text-xs">
            Instance <span class="font-mono">{{ instance.identifier }}</span>
          </p>
        </div>

        <UAlert
          v-if="!hasReported"
          title="No heartbeats yet"
          description="This instance has never reported its health. It will appear here once it posts to the health endpoint."
          color="neutral"
          variant="subtle"
          icon="i-lucide-satellite-dish"
        />

        <template v-else>
          <!-- Operational metrics -->
          <section class="flex flex-col gap-4">
            <div>
              <h2 class="text-highlighted font-semibold">
                Operational metrics
              </h2>
              <p class="text-muted text-sm">
                Reported gauges bucketed across the selected range.
              </p>
            </div>

            <Deferred :data="['metrics']">
              <template #fallback>
                <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  <USkeleton v-for="n in 3" :key="n" class="h-32 w-full" />
                </div>
              </template>

              <div v-if="charts.length === 0" class="text-muted ring-default rounded-lg p-10 text-center text-sm ring">
                No gauges reported in this range.
              </div>
              <div v-else class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <ChartPanel v-for="chart in charts" :key="chart.key" :title="chart.key" :loading="timeRange.loading.value">
                  <template #latest>
                    <span class="text-highlighted text-lg font-semibold tabular-nums">
                      {{ formatMetricValue(chart.latest, chart.kind) }}
                    </span>
                  </template>
                  <TimeSeriesChart
                    :series="chart.series"
                    :resolved="timeRange.resolved.value"
                    :height="72"
                    sparkline
                    :value-format="chartFormatter(chart.kind)"
                  />
                </ChartPanel>
              </div>
            </Deferred>
          </section>

          <!-- Recent heartbeats -->
          <section class="flex flex-col gap-4">
            <h2 class="text-highlighted font-semibold">
              Recent heartbeats
            </h2>
            <div class="divide-default ring-default divide-y rounded-lg ring">
              <div
                v-for="report in heartbeats"
                :key="report.id"
                class="flex flex-col gap-1 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div class="flex items-center gap-3">
                  <HealthBadge :state="report.status" />
                  <span class="text-highlighted font-mono text-sm">{{ report.version || '—' }}</span>
                </div>
                <div class="flex items-center gap-4">
                  <span class="text-muted text-xs">{{ metricSummary(report) }}</span>
                  <span class="text-dimmed text-xs whitespace-nowrap">{{ formatTimestamp(report.created_at) }}</span>
                </div>
              </div>
            </div>
          </section>
        </template>
      </div>
    </template>
  </UDashboardPanel>
</template>
